// ============================================
// COMPONENT - ConfirmacionModal
// ============================================

import { MdWarning, MdClose } from 'react-icons/md';

interface ConfirmacionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  variant?: 'danger' | 'warning' | 'info';
}

export const ConfirmacionModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  isLoading = false,
  variant = 'danger',
}: ConfirmacionModalProps) => {
  if (!isOpen) return null;

  const colors = {
    danger: {
      icon: '#EF4444',
      iconBg: '#FEE2E2',
      button: '#EF4444',
      buttonHover: '#DC2626',
    },
    warning: {
      icon: '#FB923C',
      iconBg: '#FFEDD5',
      button: '#FB923C',
      buttonHover: '#F97316',
    },
    info: {
      icon: '#43BFFF',
      iconBg: '#E0F2FE',
      button: '#43BFFF',
      buttonHover: '#3AABEB',
    },
  };

  const currentColors = colors[variant];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-fadeIn">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: currentColors.iconBg }}
            >
              <MdWarning className="text-2xl" style={{ color: currentColors.icon }} />
            </div>
            <h2 className="text-xl font-bold" style={{ color: '#11194F' }}>
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <MdClose className="text-2xl" style={{ color: '#11194F' }} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-700">{message}</p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            style={{ color: '#11194F' }}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="px-5 py-2.5 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{
              backgroundColor: currentColors.button,
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = currentColors.buttonHover;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = currentColors.button;
            }}
            disabled={isLoading}
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
